import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { usePedidoWhatsApp } from '@/hooks/usePedidoWhatsApp';
import SelectorVendedor from './SelectorVendedor';
import CuponCarrito from './CuponCarrito';
import { useCupon } from '@/hooks/useCupon';
import { mensajePedido } from '@/utils/pedido';
import CartDrawerItem from './CartDrawerItem';
import { formatCurrency } from '@/utils/helpers';

// ── Threshold — solo envío gratis ──
const FREE_SHIPPING_THRESHOLD = 500;

function getThresholdInfo(total: number) {
  if (total >= FREE_SHIPPING_THRESHOLD) return null;
  const pct = Math.min(100, Math.round((total / FREE_SHIPPING_THRESHOLD) * 100));
  const remaining = FREE_SHIPPING_THRESHOLD - total;
  return {
    amount: FREE_SHIPPING_THRESHOLD,
    label: 'envío gratis en La Habana',
    icon: 'ri-truck-line',
    color: '#4bbfdf',
    pct,
    remaining,
  };
}


/** "Te atiende: Ana · Cambiar" encima del botón de WhatsApp. */
function TeAtiende({ pedido }: { pedido: ReturnType<typeof usePedidoWhatsApp> }) {
  if (!pedido.vendedor) return null;
  return (
    <div className="flex items-center justify-center gap-1.5 text-[12px] text-stone-500">
      <i className="ri-user-voice-line text-xs" />
      <span>Te atiende <strong className="text-stone-800">{pedido.vendedor.nombre}</strong></span>
      {pedido.puedeCambiar && (
        <button onClick={pedido.cambiarVendedor} className="underline hover:text-stone-800 cursor-pointer">
          Cambiar
        </button>
      )}
    </div>
  );
}

export default function CartDrawer() {
  const { cart, isOpen, closeCart, removeItem, updateQty, clearCart } = useCart();
  const pedido = usePedidoWhatsApp();
  const navigate = useNavigate();
  const verOferta = () => { closeCart(); navigate('/oferta'); };
  const cupon = useCupon();
  const lineas = cart.items.map((i) => ({ producto: i.product, cantidad: i.quantity }));
  const resultado = cupon.calcular(lineas);
  const descuentoCupon = resultado?.aplica ? resultado.descuento : 0;
  const totalFinal = cart.total - descuentoCupon;
  const cuponUI = (
    <CuponCarrito config={cupon.config} codigo={cupon.codigo} resultado={resultado} onAplicar={cupon.aplicar} onQuitar={cupon.quitar} />
  );
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const isEmpty = cart.items.length === 0;
  const threshold = getThresholdInfo(cart.total);
  const [pulse, setPulse] = useState(false);

  const handleWhatsApp = () => {
    pedido.enviar(mensajePedido('¡Hola! Quiero completar mi orden:', lineas, resultado));
  };

  // Pulse the CTA button when items change
  useEffect(() => {
    if (!isEmpty) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 600);
      return () => clearTimeout(t);
    }
  }, [cart.itemCount, isEmpty]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeCart(); };
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeCart]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setTimeout(() => closeButtonRef.current?.focus(), 100);
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 transition-opacity duration-300"
        style={{
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: isOpen ? 'blur(4px)' : 'none',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        onClick={closeCart}
        aria-hidden="true"
      />

      {/* ══════════════════════════════════════════
          MOBILE — Bottom Sheet
      ══════════════════════════════════════════ */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Carrito de compras"
        className="sm:hidden print:hidden fixed bottom-0 left-0 right-0 z-50 flex flex-col"
        style={{
          maxHeight: '94svh',
          borderRadius: '24px 24px 0 0',
          background: '#faf8f5',
          transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.35s cubic-bezier(0.32,0.72,0,1)',
          willChange: 'transform',
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-2 pb-3 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 flex items-center justify-center rounded-xl"
              style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
            >
              <i className="ri-shopping-bag-3-line text-white text-base" />
            </div>
            <div>
              <h2 className="font-bold text-stone-900 text-[15px] leading-none">Mi pedido</h2>
              {!isEmpty && (
                <p className="text-[11px] text-stone-500 mt-0.5 flex flex-wrap items-center gap-x-1">
                  <span className="whitespace-nowrap">{cart.itemCount} {cart.itemCount === 1 ? 'producto' : 'productos'}</span>
                  <span className="whitespace-nowrap">· {formatCurrency(totalFinal)}</span>
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {!isEmpty && (
              <button
                onClick={clearCart}
                className="text-[11px] text-stone-500 hover:text-red-500 transition-colors cursor-pointer px-2.5 py-1.5 rounded-lg"
              >
                Vaciar
              </button>
            )}
            <button
              ref={closeButtonRef}
              onClick={closeCart}
              className="w-8 h-8 flex items-center justify-center rounded-xl text-stone-400 hover:bg-stone-100 hover:text-stone-700 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* ── Progress bar (psych trigger) ── */}
        {!isEmpty && threshold && (
          <div className="mx-4 mb-3 px-4 py-3 rounded-2xl flex-shrink-0" style={{ background: '#f5f0ea' }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <i className={`${threshold.icon} text-sm`} style={{ color: threshold.color }} />
                <span className="text-[11px] font-semibold text-stone-700">
                  Te faltan{' '}
                  <span className="font-black" style={{ color: threshold.color }}>
                    ${threshold.remaining.toFixed(0)}
                  </span>{' '}
                  para {threshold.label}
                </span>
              </div>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#e8e2d9' }}>
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${threshold.pct}%`,
                  background: `linear-gradient(90deg, ${threshold.color}99, ${threshold.color})`,
                }}
              />
            </div>
          </div>
        )}

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-4 min-h-0">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-3xl" style={{ background: '#f5f0ea' }}>
                <i className="ri-shopping-bag-3-line text-stone-400 text-3xl" />
              </div>
              <div>
                <h3 className="font-bold text-stone-900 text-base mb-1">Tu carrito está vacío</h3>
                <p className="text-sm text-stone-500">Agrega productos para armar tu pedido</p>
              </div>
              <button
                onClick={closeCart}
                className="flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-2xl text-white cursor-pointer whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
              >
                <i className="ri-store-2-line" />
                Explorar productos
              </button>
            </div>
          ) : (
            <div className="space-y-2 py-1">
              {cart.items.map((item) => (
                <CartDrawerItem
                  key={`${item.product.id}__${item.variantId ?? ''}`}
                  item={item}
                  onUpdateQty={updateQty}
                  onRemove={removeItem}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isEmpty && (
          <div className="flex-shrink-0 px-4 pt-3 pb-6 space-y-3">
            {/* Savings highlight */}
            {cart.discount > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5 rounded-xl" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.20)' }}>
                <div className="flex items-center gap-2">
                  <i className="ri-price-tag-3-fill text-emerald-400 text-sm" />
                  <span className="text-[12px] font-semibold text-emerald-400">¡Estás ahorrando!</span>
                </div>
                <span className="text-[13px] font-black text-emerald-400">-{formatCurrency(cart.discount)}</span>
              </div>
            )}

            {cuponUI}

            {/* Total row */}
            <div className="flex items-center justify-between px-1">
              <div>
                <p className="text-[11px] text-stone-500 uppercase tracking-wider">Total estimado</p>
                <p className="text-[11px] text-stone-400 mt-0.5">Envío a coordinar</p>
              </div>
              <div className="text-right">
                <p className="font-black text-stone-900 text-2xl leading-none">{formatCurrency(totalFinal)}</p>
                {(cart.discount > 0 || descuentoCupon > 0) && (
                  <p className="text-[11px] text-stone-400 line-through mt-0.5">{formatCurrency(cart.subtotal + cart.discount)}</p>
                )}
              </div>
            </div>

            <TeAtiende pedido={pedido} />

            {/* WhatsApp CTA — main action */}
            <button
              onClick={handleWhatsApp}
              className="w-full flex items-center justify-center gap-2.5 rounded-2xl text-white font-black text-[15px] cursor-pointer whitespace-nowrap relative overflow-hidden"
              style={{
                height: '58px',
                background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                transform: pulse ? 'scale(0.97)' : 'scale(1)',
                transition: 'transform 0.15s ease',
              }}
            >
              {/* Shimmer effect */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)',
                  animation: 'shimmerBtn 2.5s ease-in-out infinite',
                }}
              />
              <i className="ri-whatsapp-line text-xl relative z-10" />
              <span className="relative z-10">Pedir por WhatsApp</span>
              <i className="ri-arrow-right-line text-base relative z-10 opacity-70" />
            </button>

            {/* Secundarios: seguir comprando y la oferta en PDF */}
            <div className="grid grid-cols-2 gap-2">
              <Link
                to="/products"
                onClick={closeCart}
                className="h-10 flex items-center justify-center gap-1.5 rounded-xl text-stone-600 text-[13px] font-medium cursor-pointer whitespace-nowrap"
                style={{ background: '#f5f0ea' }}
              >
                <i className="ri-add-line text-sm" />
                Seguir agregando
              </Link>
              <button
                onClick={verOferta}
                className="h-10 flex items-center justify-center gap-1.5 rounded-xl text-[#1e2d4a] text-[13px] font-semibold cursor-pointer whitespace-nowrap border border-[#e2ddd5]"
              >
                <i className="ri-file-pdf-2-line text-sm" />
                Oferta en PDF
              </button>
            </div>
          </div>
        )}

        <style>{`
          @keyframes shimmerBtn {
            0%   { transform: translateX(-100%); }
            60%  { transform: translateX(100%); }
            100% { transform: translateX(100%); }
          }
        `}</style>
      </div>

      {/* ══════════════════════════════════════════
          DESKTOP — Side Drawer (unchanged)
      ══════════════════════════════════════════ */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Carrito de compras"
        className="hidden sm:flex print:hidden fixed top-0 right-0 h-full z-50 flex-col transition-transform duration-300 ease-out w-[460px]"
        style={{
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          background: 'linear-gradient(160deg, #faf8f5 0%, #f5f0ea 100%)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-2xl" style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
              <i className="ri-shopping-bag-3-line text-white text-lg" />
            </div>
            <div>
              <h2 className="font-bold text-white text-base leading-none">Mi pedido</h2>
              {!isEmpty && (
                <p className="text-xs text-neutral-400 mt-0.5">{cart.itemCount} {cart.itemCount === 1 ? 'producto' : 'productos'}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEmpty && (
              <button onClick={clearCart} className="text-xs text-neutral-500 hover:text-red-400 transition-colors cursor-pointer px-2.5 py-1.5 rounded-lg hover:bg-white/10">
                Vaciar
              </button>
            )}
            <button
              ref={closeButtonRef}
              onClick={closeCart}
              className="w-9 h-9 flex items-center justify-center rounded-xl text-neutral-400 hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>

        {/* Progress bar desktop */}
        {!isEmpty && threshold && (
          <div className="mx-4 mb-3 px-4 py-3 rounded-2xl flex-shrink-0" style={{ background: '#f5f0ea' }}>
            <div className="flex items-center gap-1.5 mb-2">
              <i className={`${threshold.icon} text-sm`} style={{ color: threshold.color }} />
              <span className="text-[11px] font-semibold text-stone-700">
                Te faltan <span className="font-black" style={{ color: threshold.color }}>${threshold.remaining.toFixed(0)}</span> para {threshold.label}
              </span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#e8e2d9' }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${threshold.pct}%`, background: `linear-gradient(90deg, ${threshold.color}99, ${threshold.color})` }} />
            </div>
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-4" role="list">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center h-full gap-5 py-16 text-center">
              <div className="w-24 h-24 flex items-center justify-center rounded-3xl" style={{ background: '#f5f0ea' }}>
                <i className="ri-shopping-bag-3-line text-stone-400 text-4xl" />
              </div>
              <div>
                <h3 className="font-bold text-stone-900 text-base mb-1">Tu carrito está vacío</h3>
                <p className="text-sm text-stone-500 max-w-[220px]">Agrega productos para armar tu pedido</p>
              </div>
              <button onClick={closeCart} className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-xl text-white cursor-pointer whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
                <i className="ri-store-2-line" />
                Explorar productos
              </button>
            </div>
          ) : (
            <div className="py-2 space-y-1">
              {cart.items.map((item) => (
                <CartDrawerItem
                  key={`${item.product.id}__${item.variantId ?? ''}`}
                  item={item}
                  onUpdateQty={updateQty}
                  onRemove={removeItem}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer desktop */}
        {!isEmpty && (
          <div className="flex-shrink-0 px-4 pb-5 pt-3 space-y-3">
            <div className="rounded-2xl px-4 py-3 space-y-1.5" style={{ background: '#f5f0ea' }}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Subtotal</span>
                {/* Precio normal: las ofertas y el cupón se restan en las líneas de abajo */}
                <span className="font-medium text-stone-800">{formatCurrency(cart.subtotal + cart.discount)}</span>
              </div>
              {cart.discount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-emerald-600 flex items-center gap-1"><i className="ri-price-tag-3-line text-xs" />Ofertas</span>
                  <span className="font-semibold text-emerald-600">-{formatCurrency(cart.discount)}</span>
                </div>
              )}
              {descuentoCupon > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-emerald-600 flex items-center gap-1"><i className="ri-coupon-3-line text-xs" />Cupón {resultado?.codigo}</span>
                  <span className="font-semibold text-emerald-600">-{formatCurrency(descuentoCupon)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500 flex items-center gap-1"><i className="ri-truck-line text-xs" />Envío</span>
                <span className="font-medium text-stone-400">A coordinar</span>
              </div>
              <div className="flex items-center justify-between pt-2.5 border-t border-stone-200">
                <span className="font-bold text-stone-900 text-sm">Total estimado</span>
                <span className="font-bold text-2xl text-stone-900">{formatCurrency(totalFinal)}</span>
              </div>
            </div>
            {cuponUI}
            <TeAtiende pedido={pedido} />
            <button
              onClick={handleWhatsApp}
              className="w-full h-14 flex items-center justify-center gap-3 rounded-2xl text-white text-[15px] font-bold transition-all active:scale-[0.98] cursor-pointer whitespace-nowrap relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)' }}
            >
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)', animation: 'shimmerBtn 2.5s ease-in-out infinite' }} />
              <i className="ri-whatsapp-line text-xl relative z-10" />
              <span className="relative z-10">Completar orden por WhatsApp</span>
            </button>
            <div className="grid grid-cols-2 gap-2">
              <Link to="/products" onClick={closeCart} className="h-10 flex items-center justify-center gap-2 rounded-xl text-stone-500 hover:text-stone-800 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap" style={{ background: '#f5f0ea' }}>
                <i className="ri-arrow-left-line text-sm" />
                Seguir comprando
              </Link>
              <button onClick={verOferta} className="h-10 flex items-center justify-center gap-2 rounded-xl text-[#1e2d4a] hover:bg-[#f5f0ea] text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap border border-[#e2ddd5]">
                <i className="ri-file-pdf-2-line text-sm" />
                Oferta en PDF
              </button>
            </div>
          </div>
        )}
      </div>

      <SelectorVendedor {...pedido.selector} />
    </>
  );
}
